import { INavNode, INavigationConfig } from "../../administration/partials/navigation-menu-section";
import {
  MICRO_APP_LOGIC_LAYER,
  LOGIC_LAYER_NAV_ROUTE_PREFIX,
} from "../../../config/constants";
import { IUpdateEvent } from "../types";

interface IBreadcrumbNode {
  name: string;
  pageId: string;
  isClickable: boolean;
}

/**
 * Recursively find a page in the navigation tree and return the path of ancestors.
 */
const findBreadcrumbTrail = (
  nodes: INavNode[],
  targetPageId: string,
  trail: IBreadcrumbNode[] = [],
): IBreadcrumbNode[] | null => {
  for (const node of nodes) {
    if (node.type === "page" && node.path === targetPageId) {
      return [...trail, { name: node.name, pageId: node.path, isClickable: true }];
    }
    if (node.type === "folder" && node.children?.length) {
      // Find first visible page in this folder for click navigation
      const firstVisiblePage = node.children
        .filter((c) => c.type === "page" && c.isVisibleInMenu)
        .sort((a, b) => a.order - b.order)[0];

      const result = findBreadcrumbTrail(
        node.children,
        targetPageId,
        [
          ...trail,
          {
            name: node.name,
            pageId: firstVisiblePage?.path || node.path,
            isClickable: !!firstVisiblePage,
          },
        ],
      );
      if (result) return result;
    }
  }
  return null;
};

export const useNavigationBreadcrumbTrail = (
  navigationConfigs: INavigationConfig[],
  configId: string,
  pageId: string,
  updateEventState?: IUpdateEvent,
): IBreadcrumbNode[] => {
  const config = navigationConfigs.find((c) => c.id === configId);
  if (!config) return [];

  const trail = findBreadcrumbTrail(config.children || [], pageId) || [];

  return trail.map((crumb) => {
    if (updateEventState?.data?.key === crumb.pageId) {
      return { ...crumb, name: updateEventState.data.value };
    }
    return crumb;
  });
};

/**
 * Check if the current pathname is a logic-layer navigation route.
 * Expected format: /logic-layer/app/{configId}/{pageId}
 */
export const isLogicLayerRoute = (pathname: string): boolean => {
  const segments = pathname.split("/").filter(Boolean);
  return segments[0] === MICRO_APP_LOGIC_LAYER && segments[1] === LOGIC_LAYER_NAV_ROUTE_PREFIX;
};

/**
 * Extract configId and pageId from a logic-layer navigation URL.
 */
export const parseLogicLayerRoute = (pathname: string): { configId: string; pageId: string } | null => {
  const segments = pathname.split("/").filter(Boolean);
  // /logic-layer/app/{configId}/{pageId}
  if (segments.length >= 4 && segments[0] === MICRO_APP_LOGIC_LAYER && segments[1] === LOGIC_LAYER_NAV_ROUTE_PREFIX) {
    return { configId: segments[2], pageId: segments[3] };
  }
  return null;
};
